/**
 * Orders the widgets based on priority, customer preferences and trending GT scores.
 *
 * @param {Object} widgetsData Available widgets data
 * @param {Array} favouriteSportIds List of Favourite Sports Ids
 *
 * @see
 *      https://dedsert.atlassian.net/wiki/spaces/DEV/pages/711721178/Trending+Widgets
 *      https://dedsert.atlassian.net/wiki/spaces/DEV/pages/2431844474/Favourite+Sports#Homepage-widget
 *
 * This function split widgets into 4 priority groups and use them for ordering:
 *      1. Fixed Widgets
 *          - This group has the highest priority and the widgets must be placed on assigned positions.
 *      2. Favourite Sports Widgets
 *          - This group will be created only when customer has fav sports selected.
 *          - The order of Fav Sports Widgets is determined by Sport GT Score.
 *          - Widgets from this group will take the first available slot in the list.
 *          - Widgets in this group may push down the positioning of widgets from Group 3.
 *      3. Fixed Sports Widgets
 *          - If customer has fav sports selected, the relevant widget must be filtered out from this group
 *            because it will be used in Group 2 which has higher priority.
 *          - Widgets from this group should be placed in the closest position, altough the position of the widget
 *            is not strictly fixed because the widgets from Group 2 may push down Fixed Sports Widgets.
 *      4. Trending widgets
 *          - If customer has fav sports selected, the relevant widget must be filteres out from this group
 *            because it will be used in Group 2 which has higher priority.
 *          - Widgets from this groups should be placed in the first available slot.
 *
 * @returns {Object} Ordered widgets and list of favourite widgets
 */
export const reorderWidgets = (widgetsData, favouriteSportIds) => {
    const {
        fixed = {},
        fixed_sports = {},
        trending = [],
        widgets_mapping = {},
        total = 0,
    } = widgetsData;

    const getWidgetSportId = (widgetSlug) => widgets_mapping[widgetSlug] || null;

    const isSportWidgetFavourited = (widgetSlug) => favouriteSportIds.includes(getWidgetSportId(widgetSlug));

    /**
     * Defines the initial widgets array size.
     * It takes the highest position value from fixed and fixed_sports objects.
     *
     * Although, we need to add +1 to total
     * in order to create a free slot for the Trending widgets that might get displayed at the end.
     * Why? The BE ordering starts from 1 and the 0 elem of the array will be always empty
     * which causes that there is no free slot at the end of the array.
     */
    const initialArraySize = Math.max(
        ...Object.keys(fixed).map(Number),
        ...Object.keys(fixed_sports).map(Number),
        total + 1,
    );
    const widgetsList = [...Array(initialArraySize)];

    /**
     * Group 1: Fixed Widgets.
     * Place the fixed widgets in their positions.
     */
    Object.entries(fixed).forEach(
        ([position, widgetSlug]) => (widgetsList[position] = widgetSlug)
    );

    /**
     * Group 2: Favourite Sports Widgets.
     * These widgets are ordered based on trending score.
     */
    const favouriteSportsWidgets = trending.filter(
        widgetSlug => isSportWidgetFavourited(widgetSlug)
    );

    /**
     * Group 3: Fixed Sports Widgets.
     * Favourite Sports to be filter out.
     */
    const fixedSportsWidgets = Object.entries(fixed_sports).filter(
        ([, widgetSlug]) => !isSportWidgetFavourited(widgetSlug)
    );
    const fixedSportsWidgetsFlattened = fixedSportsWidgets.map(([, widgetSlug]) => widgetSlug);

    /**
     * Group 4: Trending Sports Widgets.
     * Favourite Sports and Fixed Sports to be filter out.
     */
    const trendingSportsWidgets = trending.filter(
        widgetSlug => !isSportWidgetFavourited(widgetSlug) && !fixedSportsWidgetsFlattened.includes(widgetSlug)
    );

    const widgetOrder = widgetsList.map((widgetSlug, position) => {
        // ignore position 0 because the BE ordering starts from 1
        if (!position) {
            return false;
        }

        // current position already occupied
        if (widgetSlug) {
            return widgetSlug;
        }

        // Pick first element from Group 2
        if (favouriteSportsWidgets.length) {
            return favouriteSportsWidgets.shift();
        }

        // Pick first matching element from Group 3
        if (fixedSportsWidgets.length) {
            const [ [fixedPosition, fixedSportWidget] ] = fixedSportsWidgets;
            if (position >= fixedPosition) {
                fixedSportsWidgets.shift();

                return fixedSportWidget;
            }
        }

        // Pick first element from Group 4
        if (trendingSportsWidgets.length) {
            return trendingSportsWidgets.shift();
        }

        return false;
    }).filter(Boolean);

    const favouriteWidgets = widgetOrder.filter(
        (widgetSlug) => favouriteSportIds.includes(getWidgetSportId(widgetSlug))
    );

    return {
        widgetOrder,
        favouriteWidgets,
    };
};
